import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectLoginComplete } from '../redux/slices/authSlice';
import { useUserData } from './useUserData';
import { featureFlagService } from '../services/featureFlagService';

export const useFeatureFlag = (featureKey, facilityId = null) => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasHandledNull = useRef(false);
  const lastFacilityId = useRef(null);
  
  // Wait for user data to be loaded before making feature flag calls
  const { user } = useUserData();
  const loginComplete = useSelector(selectLoginComplete);
  
  // Consider facilities loaded when user has defaultFacility
  const facilitiesLoaded = !!user?.defaultFacility;

  useEffect(() => {
    // Handle the case where facilityId is null
    if (!facilityId) {
      if (!hasHandledNull.current && process.env.NODE_ENV === 'development') {
        console.log(`Feature '${featureKey}' called without facilityId. Defaulting to disabled for security.`);
        hasHandledNull.current = true;
      }
      // Set state to disabled for null facilityId
      setIsEnabled(false);
      setLoading(false);
      return;
    }

    // Reset the flag when we get a valid facilityId
    hasHandledNull.current = false;
    
    // Only proceed if facilityId has actually changed
    if (lastFacilityId.current === facilityId) {
      return;
    }
    lastFacilityId.current = facilityId;

    // Skip the effect entirely if we don't have a feature key or if we're not ready
    if (!featureKey || !loginComplete || !facilitiesLoaded) {
      return;
    }

    const checkFeature = async () => {
      try {
        // Use the feature flag service instead of direct API calls
        const isFeatureEnabled = await featureFlagService.isFeatureEnabled(featureKey, facilityId);
        setIsEnabled(isFeatureEnabled);
        setLoading(false);
        setError(null);
      } catch (err) {
        console.error('Error checking feature flag:', err);
        
        // Enhanced error handling with more specific messaging
        if (err.message?.includes('Access denied')) {
          console.warn(`Access denied for feature flag '${featureKey}'. Defaulting to disabled.`);
          setIsEnabled(false);
          setError(null);
        } else if (err.message?.includes('Network error')) {
          console.warn(`Network error checking feature flag '${featureKey}'. Defaulting to disabled.`);
          setIsEnabled(false);
          setError(null);
        } else if (err.message?.includes('Feature flag system temporarily unavailable')) {
          console.warn(`Feature flag system not initialized. Run 'npm run setup-features' to initialize.`);
          console.warn(`Feature '${featureKey}' defaulting to disabled until system is initialized.`);
          setIsEnabled(false);
          setError(null);
        } else if (err.message?.includes('Facility not found')) {
          console.warn(`Feature '${featureKey}' not found. It may need to be created in the admin interface.`);
          setIsEnabled(false);
          setError(null);
        } else {
          // Log detailed error information for debugging
          console.error(`Unexpected error checking feature flag '${featureKey}':`, {
            message: err.message,
            facilityId: facilityId
          });
          setError(err);
          setIsEnabled(false);
        }
        setLoading(false);
      }
    };

    checkFeature();
  }, [featureKey, facilityId, facilitiesLoaded, loginComplete]);

  return { isEnabled, loading, error };
};

// Usage example:
/*
const MyComponent = ({ facilityId }) => {
  const { isEnabled, loading } = useFeatureFlag('feature_key', facilityId);

  if (loading) return <div>Loading...</div>;

  return isEnabled ? (
    <div>Feature is enabled!</div>
  ) : (
    <div>Feature is disabled</div>
  );
};
*/ 